import { Injectable } from '@angular/core';
import { Observable, throwError } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { Apollo } from "apollo-angular";
import gql from "graphql-tag";
import {
  GetCompaniesGQL,
  RegisterCompanyGQL,
  RegisterCompanyInput,
  GetCompanyCategoriesGQL,
  GetCompanyByIdGQL,
  UpdateCompanyGQL,
  Companies_Set_Input,
  Companies_Insert_Input,
  RegisterBlueCompanyGQL,
  CompanyData,
  GetUserSeeSettingIdGQL,
  GetAllAddressGQL,
  InsertCompanyGQL,
  InsertCompanyMutationVariables,
  DeleteCompanyGQL,
  UpdateCompanyMutationVariables,
  UpdateAddressMutationVariables,
  UpdateAddressGQL,
  GetPartnerCompaniesGQL
} from "../../generated/graphql";

// Companies visible to the logged-in user (the backend scopes them by role from the session).
const GET_VISIBLE_COMPANIES = gql`
  query GetAllCompanies($filter: GetAllCompaniesFilter) {
    getAllCompanies(filter: $filter) {
      id company_name partita codice_fiscale codice_ateco
      is_partner partner_company_id partner_company_name
      sales_manager_id sales_reference_name sales_reference_role
      logo_document_id address_id indirizzo citta provincia cap
      relations
    }
  }
`;

/** Why a sales user sees a company (getAllCompanies `relations`). */
export type CompanyRelation = 'works_here' | 'sales_reference' | 'team_works_here' | 'team_sales_reference';

export const COMPANY_RELATION_LABELS: Record<CompanyRelation, string> = {
  works_here: 'Ci lavoro',
  sales_reference: 'Sono il riferimento',
  team_works_here: 'Ci lavora il mio team',
  team_sales_reference: 'Riferimento del mio team',
};

export interface VisibleCompaniesFilter {
  search?: string | null;
  company_id?: string | null;
}

const FIND_COMPANY_BY_PARTITA = gql`
  query findCompanyByPartita($partita: String!) {
    companies(where: {partita: {_eq: $partita}}, limit: 1) {
      id
    }
  }
`;

const INSERT_COMPANY_ADDRESS = gql`
  mutation insertCompanyAddress(
    $cap: String!
    $citta: String!
    $indirizzo: String!
    $provincia: String!
  ) {
    insert_adresses_one(object: {
      cap: $cap
      citta: $citta
      indirizzo: $indirizzo
      provincia: $provincia
    }) {
      id
    }
  }
`;

const ATTACH_COMPANY_ADDRESS = gql`
  mutation attachCompanyAddress($companyId: uuid!, $addressId: uuid!) {
    update_companies_by_pk(
      pk_columns: {id: $companyId}
      _set: {address_id: $addressId}
    ) {
      id
      address_id
    }
  }
`;

@Injectable({
  providedIn: 'root'
})
export class CompaniesService {
  constructor(
    private getCompaniesGQL: GetCompaniesGQL,
    private registerCompanyGQL: RegisterCompanyGQL,
    private getUserSeeSettingIdGQL: GetUserSeeSettingIdGQL,
    private getCompanyCategoriesGQL: GetCompanyCategoriesGQL,
    private getCompanyByIdGQL: GetCompanyByIdGQL,
    private updateCompanyGQL: UpdateCompanyGQL,
    private registerBlueCompanyGQL: RegisterBlueCompanyGQL,
    private insertCompanyGQL: InsertCompanyGQL,
    private getAllAddressGQL: GetAllAddressGQL,
    private deleteCompanyGQL: DeleteCompanyGQL,
    private updateAddressGQL: UpdateAddressGQL,
    private getPartnerCompaniesGQL: GetPartnerCompaniesGQL,
    private apollo: Apollo,
  ) { }

  getCompanies($id: string): Observable<any> {
    return this.getCompaniesGQL.fetch({_neq: $id}).pipe(map((result) => result.data.companies));
  };

  /**
   * Companies visible to the logged-in user (getAllCompanies Action, scoped by the backend).
   * Rows are also mapped to the legacy shape (isPartner, companies_adress, partner_company_name)
   * used by the existing company dropdowns and dialogs.
   */
  getVisibleCompanies(filter: VisibleCompaniesFilter = {}): Observable<any[]> {
    // Omit unused filter fields: the backend rejects empty strings.
    const cleanFilter: VisibleCompaniesFilter = {};
    if (filter.search?.trim()) cleanFilter.search = filter.search.trim();
    if (filter.company_id) cleanFilter.company_id = filter.company_id;
    const variables = { filter: Object.keys(cleanFilter).length ? cleanFilter : null };
    console.log('[CompanyDebug][query] getAllCompanies request', variables);
    return this.apollo.query<any>({
      query: GET_VISIBLE_COMPANIES,
      variables,
      fetchPolicy: 'network-only',
    }).pipe(
      map(result => {
        console.log('[CompanyDebug][query] getAllCompanies raw response', {
          errors: result.errors || null,
          count: result.data?.getAllCompanies?.length ?? null,
          first: result.data?.getAllCompanies?.[0] ?? null,
        });
        if (result.errors?.length) throw new Error(result.errors.map((error: any) => error.message).join('; '));
        return (result.data?.getAllCompanies || []).map((row: any) => this.toLegacyCompany(row));
      }),
      catchError(error => {
        console.error('[CompanyDebug][query] getAllCompanies FAILED', {
          message: error?.message,
          graphQLErrors: error?.graphQLErrors,
          networkError: error?.networkError?.error ?? error?.networkError,
        });
        return throwError(() => error);
      }),
    );
  }

  getAllCompanies(): Observable<any> {
    return this.getVisibleCompanies();
  };

  getCompanyOptions(): Observable<any> {
    return this.getVisibleCompanies();
  };

  private toLegacyCompany(row: any): any {
    const hasAddress = row.address_id || row.indirizzo || row.citta || row.cap || row.provincia;
    return {
      ...row,
      isPartner: Boolean(row.is_partner),
      relations: Array.isArray(row.relations) ? row.relations : [],
      companies_adress: hasAddress
        ? { id: row.address_id, indirizzo: row.indirizzo, cap: row.cap, citta: row.citta, provincia: row.provincia }
        : null,
      partner_company_name: row.partner_company_id
        ? { id: row.partner_company_id, company_name: row.partner_company_name }
        : null,
      partner_company_label: row.partner_company_name || null,
    };
  }

  /** Id of the company with this P. IVA, or null. */
  async findCompanyIdByPartita(partita: string): Promise<string | null> {
    const value = String(partita || '').trim();
    if (!value) return null;
    const result: any = await this.apollo.query({
      query: FIND_COMPANY_BY_PARTITA,
      variables: { partita: value },
      fetchPolicy: 'network-only',
    }).toPromise();
    return result?.data?.companies?.[0]?.id || null;
  }

  insertCompanyOne($input: InsertCompanyMutationVariables): Observable<any> {
    return this.insertCompanyGQL.mutate({ codice_ateco: $input.codice_ateco, codice_fiscale: $input.codice_fiscale, company_name: $input.company_name, partita: $input.partita, cap: $input.cap, citta: $input.citta, indirizzo: $input.indirizzo, provincia: $input.provincia, isPartner: $input.isPartner, partner_company_id: $input.partner_company_id, sales_manager_id:$input.sales_manager_id});
  }

  deleteCompany(id: any): Observable<any> {
    return this.deleteCompanyGQL.mutate({ id: id});
  }

  updateCompany($set: UpdateCompanyMutationVariables): Observable<any> {
    return this.updateCompanyGQL.mutate({ id: $set.id, codice_ateco: $set.codice_ateco, codice_fiscale: $set.codice_fiscale , company_name: $set.company_name, partita: $set.partita, isPartner: $set.isPartner, partner_company_id: $set.partner_company_id, sales_manager_id: $set.sales_manager_id});
  }

  updateAddress($set: UpdateAddressMutationVariables): Observable<any> {
    return this.updateAddressGQL.mutate({ id: $set.id, cap: $set.cap, citta: $set.citta , indirizzo: $set.indirizzo, provincia: $set.provincia});
  }

  saveCompanyAddress(
    companyId: string,
    addressId: string | null,
    address: Omit<UpdateAddressMutationVariables, 'id'>,
  ): Observable<any> {
    if (addressId) {
      return this.updateAddress({ ...address, id: addressId } as UpdateAddressMutationVariables);
    }

    return this.apollo.mutate<any>({
      mutation: INSERT_COMPANY_ADDRESS,
      variables: address,
    }).pipe(
      switchMap(result => {
        if (result.errors?.length) {
          throw new Error(result.errors.map((error: any) => error.message).join('; '));
        }
        const newAddressId = result.data?.insert_adresses_one?.id;
        if (!newAddressId) throw new Error('Creazione indirizzo non completata.');
        return this.apollo.mutate<any>({
          mutation: ATTACH_COMPANY_ADDRESS,
          variables: { companyId, addressId: newAddressId },
        });
      }),
      map(result => {
        if (result.errors?.length) {
          throw new Error(result.errors.map((error: any) => error.message).join('; '));
        }
        if (!result.data?.update_companies_by_pk?.id) {
          throw new Error('Collegamento indirizzo non completato.');
        }
        return result;
      }),
    );
  }


  getPartnerCompanies(): Observable<any> {
    return this.getVisibleCompanies().pipe(map(companies => companies.filter(company => company.isPartner)));
  };


  getCompanyById($id: string): Observable<any> {
    return this.getCompanyByIdGQL.fetch({ id: $id}).pipe(map((result) => result.data.companies_by_pk));
  };

  registerRedCompany($input: RegisterCompanyInput): Observable<any> {
    return this.registerCompanyGQL.mutate({ company: $input.company, user: $input.user, user_company: $input.user_company, dipendentiList: $input.dipendentiList, documentList: $input.documentList});
  }

  registerBlueCompany($input: CompanyData): Observable<any> {
    return this.registerBlueCompanyGQL.mutate({ data: $input});
  }

  getCompanyCategories(): Observable<any> {
    return this.getCompanyCategoriesGQL.fetch({}).pipe(map((result) => result.data.company_categories));
  };
}
